import {
  type IssueMetadataCorsiaDefault,
  type RegulatoryScheme,
  SustainabilityTier,
} from '../../unit-meta/index.js';
import {
  getActualGHGIntensityAirportDelivery,
  getValue,
  tier1Schemes,
  tierASchemes,
  tierCFeedStock,
} from '../constants.js';

export namespace CalculationsV1CorsiaDefault {
  export const equations = {
    [SustainabilityTier.A]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaDefault) => {
      return (
        getValue(greenhouseGas?.coreLCAValue)
        + getValue(greenhouseGas.inducedLandUseChange)
        + getValue(greenhouseGas.ghgDisplacementEmissions)
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
      );
    },

    [SustainabilityTier.B]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaDefault) => {
      return (
        getValue(greenhouseGas?.coreLCAValue)
        + getValue(greenhouseGas?.inducedLandUseChange)
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
      );
    },

    [SustainabilityTier.C]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaDefault) => {
      return (
        getValue(greenhouseGas?.coreLCAValue)
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        + getValue(greenhouseGas?.inducedLandUseChange)
        - getValue(greenhouseGas?.recyclingEmissionsCredits)
        - getValue(greenhouseGas?.landfillEmissionsCredits)
      );
    },
  };

  const verifyTierB = (metadata: IssueMetadataCorsiaDefault) => {
    const {
      product: {
        jetFuelBaselineCarbonIntensity,
        feedStock,
      },
      certification: { isLowILUCRiskConfirmed },
    } = metadata;

    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (
      !(1 - (equations[SustainabilityTier.B](metadata) / baseline) >= 0.6)
    ) {
      return SustainabilityTier.C;
    }

    if (feedStock?.wasCertifiedAsProductionResidue) {
      return SustainabilityTier.B;
    }

    if (isLowILUCRiskConfirmed) {
      return SustainabilityTier.B;
    }

    return SustainabilityTier.C;
  };

  export const calculateSustainabilityTierForCorsiaDefault = (
    metadata: IssueMetadataCorsiaDefault,
  ) => {
    const {
      product: {
        feedStock,
        conversionProcess,
        jetFuelBaselineCarbonIntensity,
      },
      certification: { scheme, isLowILUCRiskConfirmed, isLowDisplacementRiskConfirmed },
      batch: { regulatorySchemesDeclaration },
      greenhouseGas: { ghgDisplacementEmissions },
    } = metadata;
    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (feedStock?.name && tierCFeedStock.includes(feedStock.name)) {
      return SustainabilityTier.C;
    }

    if (conversionProcess === 'ptl' && !tierASchemes.includes(scheme)) {
      return SustainabilityTier.C;
    }

    if (!isLowILUCRiskConfirmed) {
      return verifyTierB(metadata);
    }

    if (
      !feedStock.wasCertifiedAsProductionResidue
      && regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
        tier1Schemes.includes(x),
      )
    ) {
      return SustainabilityTier.C;
    }

    if (!ghgDisplacementEmissions) {
      return verifyTierB(metadata);
    }

    if (!isLowDisplacementRiskConfirmed) {
      return verifyTierB(metadata);
    }
    if (regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
      tier1Schemes.includes(x),
    )) {
      return SustainabilityTier.C;
    }

    if (
      !(1 - (equations[SustainabilityTier.A](metadata) / baseline) >= 0.6)
    ) {
      return verifyTierB(metadata);
    }

    return SustainabilityTier.A;
  };
}
